/**
 * Where a player's balls pitched, in the man-of-the-match post's pitch map (gemini-cv-project
 * scripts/bounce_detection/motm/page/map.js, `MotmMap.draw`, ported unchanged in its geometry and
 * colours): the pitch seen from behind the bowler's end, drawn 2.4x its real width and split into the
 * pipeline's twelve zones (lengths Y 0-2, F 2-4, G 4-7, S 7-20 m from the batting stumps; lines off /
 * stumps / leg), a full-toss band behind the stumps, and five gold steps of the busiest zone.
 *
 * A live session has no scorecard, so each ball counts one: a zone's number is how many of the
 * player's balls pitched there. The zone of a ball is the published length and line class
 * (bucketize in the pipeline), never this drawing's equal thirds; a ball with no bounce read goes to
 * the full-toss band only where the pipeline called a full toss, else it is counted in the footnote.
 */
import { useEffect, useId, useRef } from "react";

export interface MapBall { zone: string | null; verdict: "placed" | "full toss" | "not shown" }

const PITCH = { len: 20.12, halfW: 1.525, stumpH: 0.711, stumpX: 0.114, popping: 1.22 };
const ZONES: [string, string, number, number][] = [["YORKER", "Y", 0, 2], ["FULL", "F", 2, 4], ["GOOD", "G", 4, 7], ["SHORT", "S", 7, 20]];
const LINES: [string, string][] = [["OFF", "O"], ["STUMPS", "S"], ["LEG", "L"]];
const HEAT = ["#3a3122", "#5e4a26", "#8a6a2c", "#bb8f34", "#f2c14e"];
const HEAT_INK = ["#f4f6fa", "#f4f6fa", "#f4f6fa", "#14110a", "#14110a"];
const THIRD = PITCH.halfW / 3;
const WIDE = 2.4;
const DRAW_TO = 13;
const FT: [number, number] = [2.5, 5.4];
const NS = "http://www.w3.org/2000/svg";
type V3 = [number, number, number];

function el(tag: string, attrs: Record<string, string | number | null>, parent?: Element): SVGElement {
  const e = document.createElementNS(NS, tag) as SVGElement;
  for (const [k, v] of Object.entries(attrs)) if (v != null) e.setAttribute(k, String(v));
  if (parent) parent.appendChild(e);
  return e;
}
const fx = (v: number) => (Math.round(v * 10) / 10).toString();
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
const sub = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const dot = (a: V3, b: V3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a: V3, b: V3): V3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const norm = (a: V3): V3 => {
  const l = Math.hypot(a[0], a[1], a[2]);
  return [a[0] / l, a[1] / l, a[2] / l];
};

interface Cam { f: number; project: (p: V3) => V3 }
function camera(pos: V3, target: V3, f: number, cx: number, cy: number): Cam {
  const fwd = norm(sub(target, pos));
  const right = norm(cross(fwd, [0, 0, 1]));
  const up = cross(right, fwd);
  return { f, project: (p) => {
    const d = sub(p, pos);
    const z = dot(d, fwd);
    return [cx + (f * dot(d, right)) / z, cy - (f * dot(d, up)) / z, z];
  } };
}
function fitCamera(pos: V3, target: V3, points: V3[], box: { x: number; y: number; w: number; h: number }): Cam {
  const unit = camera(pos, target, 1, 0, 0);
  let u0 = Infinity, u1 = -Infinity, v0 = Infinity, v1 = -Infinity;
  for (const p of points) {
    const q = unit.project(p);
    u0 = Math.min(u0, q[0]); u1 = Math.max(u1, q[0]); v0 = Math.min(v0, q[1]); v1 = Math.max(v1, q[1]);
  }
  const f = Math.min(box.w / (u1 - u0), box.h / (v1 - v0));
  return camera(pos, target, f, box.x + (box.w - f * (u1 - u0)) / 2 - f * u0, box.y + (box.h - f * (v1 - v0)) / 2 - f * v0);
}
const ptsAttr = (cam: Cam, list: V3[]) => list.map((p) => { const q = cam.project(p); return `${fx(q[0])},${fx(q[1])}`; }).join(" ");
const rectXY = (x0: number, x1: number, y0: number, y1: number): V3[] => [[x0, y0, 0], [x1, y0, 0], [x1, y1, 0], [x0, y1, 0]];
function line3(g: Element, cam: Cam, a: V3, b: V3, width: number, color: string) {
  const p = cam.project(a);
  const q = cam.project(b);
  const w = clamp((cam.f * width) / ((p[2] + q[2]) / 2), 0.7, 8);
  el("line", { x1: fx(p[0]), y1: fx(p[1]), x2: fx(q[0]), y2: fx(q[1]), stroke: color, "stroke-width": w.toFixed(2), "stroke-linecap": "round" }, g);
}
function drawStumps(g: Element, cam: Cam, y: number, k: number) {
  const h = PITCH.stumpH * k;
  const sx = PITCH.stumpX * k;
  for (const x of [-sx, 0, sx]) {
    line3(g, cam, [x, y, 0], [x, y, h], 0.06 * k, "rgba(30,24,16,0.55)");
    line3(g, cam, [x, y, 0], [x, y, h], 0.036 * k, "#efe4c8");
  }
  line3(g, cam, [-sx - 0.01 * k, y, h + 0.012 * k], [sx + 0.01 * k, y, h + 0.012 * k], 0.024 * k, "#f6eedb");
}

/** Balls per zone (and the full-toss band), as map.js `cells` with each ball worth one. */
export function mapCells(balls: MapBall[]) {
  const pitched = balls.filter((b) => b.verdict === "placed");
  const list = ZONES.flatMap((z) => LINES.map((ln, c) => ({ zone: z, line: ln, x0: -PITCH.halfW + 2 * THIRD * c, n: pitched.filter((b) => b.zone === z[1] + ln[1]).length })));
  const ft = balls.filter((b) => b.verdict === "full toss").length;
  const max = Math.max(ft, ...list.map((x) => x.n));
  return { list, ft, max, unseen: balls.filter((b) => b.verdict === "not shown").length };
}

function draw(sv: SVGSVGElement, balls: MapBall[], selected: string | null, onZone?: (z: string) => void) {
  const focusedZone = sv.contains(document.activeElement) ? document.activeElement?.getAttribute("data-zone") : null;
  const o = { W: 880, H: 760, labelW: 200, maxW: 640, s: 2.2 };
  const s = o.s;
  const C = mapCells(balls);
  const step = (r: number) => (r && C.max ? Math.min(4, Math.ceil((5 * r) / C.max) - 1) : -1);
  while (sv.firstChild) sv.removeChild(sv.firstChild);
  sv.setAttribute("viewBox", `0 0 ${o.W} ${o.H}`);
  const hw = PITCH.halfW * WIDE;
  const far = PITCH.len + FT[1] + 0.1;
  const real = fitCamera([0, 1.5, 13.5], [0, 15.2, 0], [[-hw, 7.1, 0], [hw, 7.1, 0], [-hw, far, 0], [hw, far, 0], [0, PITCH.len, 2 * PITCH.stumpH + 0.05]],
    { x: o.labelW, y: 8 * s, w: Math.min(o.W - o.labelW - 8 * s, o.maxW), h: o.H - 38 * s });
  const cam: Cam = { f: real.f, project: (p) => real.project([p[0] * WIDE, p[1], p[2]]) };
  const g = el("g", {}, sv);
  const labels: (() => void)[] = [];
  el("polygon", { points: ptsAttr(cam, rectXY(-PITCH.halfW, PITCH.halfW, PITCH.len, 21.0)), fill: "var(--pitch-a)" }, g);
  const label = (x: number, y: number, n: number, k: number) => {
    const t = el("text", { x: fx(x), y: fx(y + 6 * s), "text-anchor": "middle", class: k < 0 ? "heat-n is-none" : "heat-n", fill: k < 0 ? "var(--ink-3)" : HEAT_INK[k]!, "font-size": (k < 0 ? 15 : 17) * s }, g);
    t.textContent = n ? String(n) : "–";
  };
  for (const x of C.list) {
    const lo = x.zone[2];
    const hi = Math.min(x.zone[3], DRAW_TO);
    const k = step(x.n);
    const cell = el("polygon", { points: ptsAttr(cam, rectXY(x.x0, x.x0 + 2 * THIRD, PITCH.len - hi, PITCH.len - lo)), fill: k < 0 ? "var(--pitch-b)" : HEAT[k]!, stroke: "var(--pitch-a)", "stroke-width": 2 * s, "stroke-linejoin": "round", class: "heat-cell" }, g);
    const code = x.zone[1] + x.line[1];
    cell.setAttribute("data-zone", code);
    cell.setAttribute("data-n", String(x.n));
    if (onZone && x.n > 0) {
      cell.classList.add("pick");
      cell.setAttribute("role", "button");
      cell.setAttribute("tabindex", "0");
      cell.setAttribute("aria-label", `${x.zone[0].toLowerCase()}, ${x.line[0].toLowerCase()}: ${x.n} balls`);
      cell.setAttribute("aria-pressed", String(selected === code));
      cell.addEventListener("click", () => onZone(code));
      cell.addEventListener("keydown", (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onZone(code); } });
    }
    if (selected === code) {
      cell.setAttribute("stroke", "#f4f6fa");
      cell.setAttribute("stroke-width", String(3 * s));
    }
    const m = cam.project([x.x0 + THIRD, PITCH.len - (lo + hi) / 2, 0]);
    labels.push(() => label(m[0], m[1], x.n, k));
  }
  const kf = step(C.ft);
  const y0 = PITCH.len + FT[0];
  const y1 = PITCH.len + FT[1];
  const band = el("polygon", { points: ptsAttr(cam, rectXY(-PITCH.halfW, PITCH.halfW, y0, y1)), fill: kf < 0 ? "var(--pitch-b)" : HEAT[kf]!, stroke: "rgba(244,246,250,0.55)", "stroke-width": 1.4 * s, "stroke-dasharray": `${5 * s} ${4 * s}`, "stroke-linejoin": "round", class: "heat-cell ft-band" }, g);
  band.setAttribute("data-zone", "FT");
  band.setAttribute("data-n", String(C.ft));
  if (onZone && C.ft > 0) {
    band.classList.add("pick");
    band.setAttribute("role", "button");
    band.setAttribute("tabindex", "0");
    band.setAttribute("aria-label", `Full toss: ${C.ft} balls`);
    band.setAttribute("aria-pressed", String(selected === "FT"));
    band.addEventListener("click", () => onZone("FT"));
    band.addEventListener("keydown", (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onZone("FT"); } });
  }
  if (selected === "FT") band.setAttribute("stroke", "#f4f6fa");
  const stTop = real.project([0, PITCH.len, 2 * PITCH.stumpH + 0.05])[1];
  let fm = cam.project([0, y0 + 0.5 * (y1 - y0), 0]);
  for (let t = 0.5; t <= 0.9001; t += 0.05) {
    fm = cam.project([0, y0 + t * (y1 - y0), 0]);
    if (fm[1] + 6 * s <= stTop - 3 * s) break;
  }
  const ftAt = fm;
  labels.push(() => label(ftAt[0], ftAt[1], C.ft, kf));
  for (const z of [...ZONES, ["FULL TOSS", "T", -FT[1], -FT[0]] as [string, string, number, number]]) {
    const lo = z[2];
    const hi = Math.min(z[3], DRAW_TO);
    const p = cam.project([-PITCH.halfW, PITCH.len - (lo + hi) / 2, 0]);
    const t = el("text", { x: fx(p[0] - 10 * s), y: fx(p[1] + 4.5 * s), "text-anchor": "end", class: "zone-name", "font-size": 14 * s }, g);
    t.textContent = z[0];
  }
  for (const y of [PITCH.len, PITCH.len - PITCH.popping]) line3(g, cam, [-PITCH.halfW, y, 0], [PITCH.halfW, y, 0], 0.03, "rgba(255,255,255,0.45)");
  labels.forEach((f) => f());
  drawStumps(g, real, PITCH.len, 2);
  LINES.forEach((ln, c) => {
    const p = cam.project([-PITCH.halfW + THIRD * (2 * c + 1), 7.1, 0]);
    const t = el("text", { x: fx(p[0]), y: fx(p[1] + 22 * s), "text-anchor": "middle", class: "tick-strong", "font-size": 13 * s }, g);
    t.textContent = ln[0];
  });
  if (focusedZone) sv.querySelector<SVGElement>(`[data-zone="${focusedZone}"]`)?.focus({ preventScroll: true });
}

/** `onZone`: a zone with balls in it is a button; `selected` is outlined. */
export function PitchMap({ balls, title, selected = null, onZone }: { balls: MapBall[]; title?: string; selected?: string | null; onZone?: (z: string) => void }) {
  const summaryId = useId();
  const ref = useRef<SVGSVGElement | null>(null);
  const key = JSON.stringify(balls);
  const cb = useRef(onZone);
  cb.current = onZone;
  useEffect(() => {
    if (ref.current) draw(ref.current, balls, selected, onZone ? (z) => cb.current?.(z) : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, selected, !!onZone]);
  const c = mapCells(balls);
  const pitched = balls.length - c.ft - c.unseen;
  return (
    <section className="lv-map" data-testid="pitch-map" data-balls={balls.length}>
      {title ? <div className="lv-map-h">{title}</div> : null}
      <svg ref={ref} role={onZone ? "group" : "img"} aria-label="Balls by pitch zone" aria-describedby={summaryId} />
      <p className="sr-only" id={summaryId}>{c.list.map(x => `${x.zone[0].toLowerCase()}, ${x.line[0].toLowerCase()}: ${x.n} balls`).join(". ")}. Full toss: {c.ft} balls. Not seen: {c.unseen} balls.</p>
      <p className="lv-map-foot">{balls.length} balls · {pitched} pitched · {c.ft} full toss · {c.unseen} not seen{onZone ? " · click a zone to list its clips" : ""}</p>
    </section>
  );
}
